/**
 * SOVRANOVA first-run onboarding — "Warm Welcome, blended"
 * Design: design_handoff_onboarding (Aug 2026) · artboards 2a (mobile),
 * 3a (desktop rail) and 4a (baseline check-in).
 *
 * Contract is unchanged: <OnboardingFlow onComplete={fn} />.
 * Saves the same profile fields via POST /auth/onboarding as before.
 * The optional baseline check-in (5 worded 1–5 scales) saves via
 * POST /outcomes/baseline as instrument SOV-BASE-5.
 */
import { useEffect, useRef, useState } from 'react'
import { api } from '../../services/api'
import { resolveRegion } from '../../services/crisisResources'
import './onboarding.css'

const AUTO_ADVANCE_MS = 280   // show the selected pill briefly, then move on
const DONE_HOLD_MS    = 1800  // how long "Welcome home" stays before routing

const STEPS = [
  { id: 'intro', railNote: 'A gentle start.',
    title: 'Hello. So glad you made it.',
    sub: "Let's get to know each other a little — gently, and at your pace. A few small steps, about two minutes.",
    cta: "Let's go" },
  { id: 'name', railNote: 'Just between us.', type: 'text', field: 'preferredName',
    title: 'First things first — what do we call you?',
    sub: 'Whatever your friends call you works perfectly.',
    placeholder: 'Your name' },
  { id: 'concern', railNote: "Whatever's on your mind.", type: 'choice', field: 'primaryConcern',
    title: a => a.preferredName && a.preferredName.trim()
      ? `Lovely to meet you, ${a.preferredName.trim()}. What brings you in?`
      : 'What brings you in today?',
    sub: 'Pick whatever rings truest — you can always change it.',
    options: [
      { value: 'anxiety',       label: 'Anxiety and overwhelm' },
      { value: 'trauma',        label: 'Trauma and past pain' },
      { value: 'sleep',         label: 'Sleep and exhaustion' },
      { value: 'burnout',       label: 'Burnout and recovery' },
      { value: 'relationships', label: 'Relationships and connection' },
      { value: 'general',       label: 'General wellbeing' },
    ] },
  { id: 'body', railNote: 'Your body knows.', type: 'choice', field: 'nervousSystemState',
    title: "How's your body feeling right now?",
    sub: 'No wrong answers — just checking in with where you are.',
    options: [
      { value: 'calm',      label: 'Calm and grounded' },
      { value: 'anxious',   label: 'Tense or anxious' },
      { value: 'exhausted', label: 'Depleted or exhausted' },
      { value: 'numb',      label: 'Numb or disconnected' },
      { value: 'mixed',     label: 'Mixed — hard to say' },
    ] },
  { id: 'energy', railNote: 'However much you have.', type: 'choice', field: 'currentEnergy',
    title: "And how's your energy holding up?",
    sub: "There's no right answer. Just what is true.",
    options: [
      { value: 'good',        label: 'Good — I have energy' },
      { value: 'low',         label: 'Low — running on empty' },
      { value: 'wired_tired', label: 'Wired but exhausted' },
      { value: 'flat',        label: 'Very flat — barely here' },
    ] },
  { id: 'safety', railNote: 'Care, with honesty.', type: 'choice', field: 'safetyAcknowledged',
    title: 'One gentle note',
    sub: "We're here to support your wellbeing — not to replace professional clinical care. If you're ever in crisis, we'll connect you with the right help straight away, no questions asked.",
    options: [{ value: true, label: 'I understand' }] },
  // Region — asked explicitly, once, because it decides which EMERGENCY and
  // crisis services this person is shown. We never infer it silently: a wrong
  // guess here is a safety defect, not a cosmetic one. Pre-selected from the
  // browser's own locale/timezone so for most people it is a single tap.
  { id: 'region', railNote: 'So we show the right help.', type: 'choice', field: 'region',
    title: 'Where are you, roughly?',
    sub: "This is only so we can show you the right emergency and support services for where you are. You can change it any time in Settings — please do if you travel.",
    options: [
      { value: 'AU', label: 'Australia' },
      { value: 'NZ', label: 'New Zealand' },
      { value: 'GB', label: 'United Kingdom' },
      { value: 'IE', label: 'Ireland' },
      { value: 'US', label: 'United States' },
      { value: 'CA', label: 'Canada' },
      { value: 'EU', label: 'Elsewhere in Europe' },
      { value: 'XX', label: 'Somewhere else' },
    ] },
  { id: 'checkin', railNote: 'Almost there.', type: 'choice', field: 'wantsBaselineAssessment',
    manualAdvance: true,
    title: 'Fancy a quick check-in?',
    sub: "Five quiet minutes to notice where you're starting from — so we can show you how far you've come. Completely optional.",
    cta: 'Come on in',
    options: [
      { value: true,  label: 'Sure — 5 minutes' },
      { value: false, label: 'Maybe later' },
    ] },
]

const CHECKIN_QS = [
  { key: 'mood', label: 'Mood',
    title: "How's your mood, right now?", sub: 'First thing that comes to mind.',
    options: ['Really heavy', 'Low', 'Somewhere in between', 'Mostly okay', 'Genuinely good'] },
  { key: 'sleep', label: 'Sleep',
    title: 'How have you been sleeping?', sub: 'The last week or so.',
    options: ['Barely at all', 'Rough and broken', 'Hit and miss', 'Mostly fine', 'Deep and restful'] },
  { key: 'tension', label: 'Tension',
    title: 'How much tension is your body holding?',
    sub: 'Jaw, shoulders, chest, stomach — wherever it lives.',
    options: ['Wound tight', 'Quite a lot', 'A moderate amount', 'A little', 'Hardly any'] },
  { key: 'calm', label: 'Calm',
    title: 'How safe and settled do you feel?', sub: 'In yourself, in this moment.',
    options: ['On high alert', 'Uneasy', 'Neutral', 'Fairly settled', 'Grounded and safe'] },
  { key: 'support', label: 'Support',
    title: 'How supported do you feel by the people around you?',
    sub: 'Honestly — this stays between us.',
    options: ['Very alone', 'Not much', 'Somewhat', 'Fairly well', 'Deeply supported'] },
]

const CheckIcon = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 6 9 17l-5-5" />
  </svg>
)

export function OnboardingFlow({ onComplete }) {
  const [phase,   setPhase]   = useState('steps')  // steps | done | checkin
  const [step,    setStep]    = useState(0)
  // Pre-select the region from the browser's own locale/timezone so confirming
  // it is a single tap for most people. resolveRegion() returns 'GLOBAL' when
  // it cannot tell — mapped to the picker's 'XX' so nothing is pre-chosen
  // wrongly. The user still has to confirm; we never decide this silently.
  const [answers, setAnswers] = useState(() => {
    try {
      const guess = resolveRegion()
      if (guess && guess !== 'GLOBAL') return { region: guess === 'UK' ? 'GB' : guess }
    } catch (_) {}
    return {}
  })
  const [saving,  setSaving]  = useState(false)

  // check-in state: -1 intro, 0..4 questions, 5 recap
  const [cq,       setCq]       = useState(-1)
  const [cAnswers, setCAnswers] = useState({})
  const [cSaved,   setCSaved]   = useState(null)

  const advanceTimer = useRef(null)
  useEffect(() => () => clearTimeout(advanceTimer.current), [])

  const current  = STEPS[step]
  const title    = typeof current.title === 'function' ? current.title(answers) : current.title
  const isLast   = step === STEPS.length - 1
  const name     = (answers.preferredName || '').trim()

  const answered = () => {
    if (!current.field) return true
    const v = answers[current.field]
    if (current.type === 'text') return v != null && String(v).trim() !== ''
    return v !== undefined
  }

  const save = async () => {
    setSaving(true)
    const body = { ...answers, preferredName: name || null }
    try { await api.post('/auth/onboarding', body) }
    catch (_) {
      // One retry after a beat — the commonest failure here is an auth token
      // mid-refresh (19 Aug loop). A save that fails twice is still swallowed:
      // the device-side completion flag (App.jsx) prevents a re-ask, and the
      // answers can be re-captured in Settings later.
      try { await new Promise(r => setTimeout(r, 1500)); await api.post('/auth/onboarding', body) } catch (_) {}
    }
    setSaving(false)
  }

  const finishSteps = async (wantsCheckin) => {
    await save()
    setPhase('done')
    advanceTimer.current = setTimeout(() => {
      if (wantsCheckin) setPhase('checkin')
      else onComplete()
    }, DONE_HOLD_MS)
  }

  // Choice pills: show the selection briefly, then advance (except the final
  // step, which is confirmed by its "Come on in" CTA).
  const pick = (value) => {
    setAnswers(p => ({ ...p, [current.field]: value }))
    // Region is written to localStorage the instant it's chosen — the offline
    // crisis lifeline reads it from there, so it must not wait on the profile
    // save at the end of onboarding (which can fail, or run while offline).
    if (current.field === 'region' && value) {
      try { localStorage.setItem('sovranova_region', String(value)) } catch (_) {}
    }
    if (current.manualAdvance) return
    clearTimeout(advanceTimer.current)
    advanceTimer.current = setTimeout(() => {
      setStep(s => Math.min(s + 1, STEPS.length - 1))
    }, AUTO_ADVANCE_MS)
  }

  const handleNext = () => {
    if (!answered() || saving) return
    if (isLast) finishSteps(answers.wantsBaselineAssessment === true)
    else setStep(s => s + 1)
  }

  const handleBack = () => {
    clearTimeout(advanceTimer.current)
    setStep(s => Math.max(s - 1, 0))
  }

  // ── Baseline check-in ─────────────────────────────────────────────────────
  const saveCheckin = async (responses) => {
    try { await api.post('/outcomes/baseline', { responses }); setCSaved(true) }
    catch (_) { setCSaved(false) }
    setCq(CHECKIN_QS.length) // recap
  }

  const pickCheckin = (q, value) => {
    const next = { ...cAnswers, [q.key]: value }
    setCAnswers(next)
    clearTimeout(advanceTimer.current)
    advanceTimer.current = setTimeout(() => {
      const idx = CHECKIN_QS.findIndex(x => x.key === q.key)
      if (idx < CHECKIN_QS.length - 1) setCq(idx + 1)
      else saveCheckin(next)
    }, AUTO_ADVANCE_MS)
  }

  const checkinNext = () => {
    if (cq === -1) { setCq(0); return }
    const q = CHECKIN_QS[cq]
    if (cAnswers[q.key] === undefined) return
    if (cq < CHECKIN_QS.length - 1) setCq(cq + 1)
    else saveCheckin(cAnswers)
  }

  const checkinBack = () => {
    clearTimeout(advanceTimer.current)
    setCq(q => Math.max(q - 1, -1))
  }

  const restartCheckin = () => { setCAnswers({}); setCSaved(null); setCq(-1) }

  // ── Check-in screens (artboard 4a: sage chrome, stacked pills) ────────────
  if (phase === 'checkin') {
    const onRecap  = cq === CHECKIN_QS.length
    const onIntro  = cq === -1
    const q        = !onIntro && !onRecap ? CHECKIN_QS[cq] : null
    const headLbl  = onIntro ? 'check-in' : onRecap ? 'saved' : `${cq + 1} of 5`

    return (
      <div className="sov-ob sov-ob--sage sov-ob--checkin">
        <div className="ob-card">
          <div className="ob-main">
            <div className="ob-blob" />
            <div className="ob-head">
              <div className="ob-brand">sovranova</div>
              <div className="ob-counter">{headLbl}</div>
            </div>

            {onRecap ? (
              <div className="ob-recap">
                <h2 className="ob-recap-title">Your starting point</h2>
                <p className="ob-recap-sub">
                  {cSaved === false
                    ? "We couldn't save this just now — but you're all set to come in."
                    : "Saved. Next time you check in, we'll show you what's shifted."}
                </p>
                <div className="ob-recap-rows">
                  {CHECKIN_QS.map(x => (
                    <div key={x.key} className="ob-recap-row">
                      <span className="ob-recap-q">{x.label}</span>
                      <span className="ob-recap-a">{x.options[(cAnswers[x.key] || 1) - 1]}</span>
                    </div>
                  ))}
                </div>
                <div className="ob-recap-actions">
                  <button className="ob-outline" onClick={restartCheckin}>Restart</button>
                  <button className="ob-next ob-next--grow" onClick={onComplete}>Into the app</button>
                </div>
              </div>
            ) : (
              <>
                <div className="ob-body">
                  <h2 className="ob-title">{onIntro ? 'A quiet five minutes.' : q.title}</h2>
                  <p className="ob-sub">
                    {onIntro
                      ? "Five questions about where you are today. There's no score and no judgment — just a starting point to look back on."
                      : q.sub}
                  </p>
                  {!onIntro && (
                    <div className="ob-choices ob-choices--stack">
                      {q.options.map((label, i) => (
                        <button key={label}
                          className={`ob-pill ${cAnswers[q.key] === i + 1 ? 'is-selected' : ''}`}
                          onClick={() => pickCheckin(q, i + 1)}>
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="ob-foot">
                  {!onIntro && <button className="ob-back" onClick={checkinBack}>Go back</button>}
                  <div className="ob-spacer" />
                  <button className="ob-next" onClick={checkinNext}
                    disabled={!onIntro && cAnswers[CHECKIN_QS[cq].key] === undefined}>
                    {onIntro ? 'Begin' : 'Next'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }

  // ── Onboarding screens (2a mobile / 3a desktop) ───────────────────────────
  const TOTAL_STEPS = STEPS.length
  const stepNum = phase === 'done' ? TOTAL_STEPS : step + 1
  const railNote = phase === 'done' ? 'Welcome.' : current.railNote

  return (
    <div className="sov-ob">
      <div className="ob-card">
        <aside className="ob-rail">
          <div className="ob-rail-blob" />
          <div className="ob-rail-brand">sovranova</div>
          <div className="ob-rail-center">
            <div className="ob-rail-counter">{stepNum} of {TOTAL_STEPS}</div>
            <div className="ob-rail-track">
              <div className="ob-rail-fill" style={{ width: `${(stepNum / TOTAL_STEPS) * 100}%` }} />
            </div>
            <p className="ob-rail-note">{railNote}</p>
          </div>
        </aside>

        <div className="ob-main">
          <div className="ob-blob" />
          <div className="ob-head">
            <div className="ob-brand">sovranova</div>
            <div className="ob-counter">{stepNum} of {TOTAL_STEPS}</div>
          </div>

          {phase === 'done' ? (
            <div className="ob-done">
              <div className="ob-done-mark"><CheckIcon /></div>
              <h2 className="ob-done-title">{name ? `Welcome home, ${name}.` : 'Welcome home.'}</h2>
              <p className="ob-done-sub">We're so glad you're here.</p>
            </div>
          ) : (
            <>
              <div className="ob-body">
                <h2 className="ob-title">{title}</h2>
                {current.sub && <p className="ob-sub">{current.sub}</p>}

                {current.type === 'text' && (
                  <input className="ob-input" type="text"
                    placeholder={current.placeholder}
                    value={answers[current.field] || ''}
                    onChange={e => setAnswers(p => ({ ...p, [current.field]: e.target.value }))}
                    onKeyDown={e => e.key === 'Enter' && handleNext()}
                    autoFocus />
                )}

                {current.type === 'choice' && (
                  <div className="ob-choices">
                    {current.options.map(o => (
                      <button key={String(o.value)}
                        className={`ob-pill ${answers[current.field] === o.value ? 'is-selected' : ''}`}
                        onClick={() => pick(o.value)}>
                        {o.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="ob-foot">
                {step > 0 && <button className="ob-back" onClick={handleBack}>Go back</button>}
                <div className="ob-spacer" />
                <button className="ob-next" onClick={handleNext} disabled={!answered() || saving}>
                  {saving ? 'One moment…' : current.cta || 'Next'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
